import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getChatList, deleteChat } from "../backendApi/backend";
import { 
  MenuFoldOutlined, 
  MenuUnfoldOutlined, 
  HomeOutlined,
  MessageOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Menu, ConfigProvider, Typography, message, Popconfirm } from 'antd';
import { useGlobalContext } from '../GlobalContext';

type MenuItem = Required<MenuProps>['items'][number];

const Sidebar: React.FC<{ toggleCollapsed: () => void; collapsed: boolean }> = ({ 
  toggleCollapsed, 
  collapsed 
}) => {
  const [chatItems, setChatItems] = useState<MenuItem[]>([]);
  const { isMobile } = useGlobalContext();
  const navigate = useNavigate();
  const username = localStorage.getItem('user');

  // Fetch chat list on component mount
  useEffect(() => {
    fetchChats();
  }, []);

  // Handle chat deletion
  const handleDeleteChat = async (e: any, chatId: string) => {
    e?.stopPropagation();
    try {
      await deleteChat(chatId);
      message.success('Chat deleted successfully');
      // Refresh the chat list
      fetchChats();
      // If we're currently viewing the deleted chat, redirect to home
      if (window.location.pathname === `/chat/${chatId}`) {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      message.error('Failed to delete chat');
    }
  };

  const fetchChats = async () => {
    try {
      const response = await getChatList();
      const chats = response.chats.slice(-15); // Show only latest 15 chats
      
      const chatMenuItems = chats.map((chat: { uuid: string; first_message: string }) => ({
        key: `/chat/${chat.uuid}`,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>
              {chat.first_message ? 
                (chat.first_message.length > 15 ? 
                  `${chat.first_message.substring(0, 15)}...` : 
                  chat.first_message) : 
                'New conversation'}
            </span>
            <Popconfirm
              title="Delete chat"
              description="Are you sure you want to delete this chat?"
              onConfirm={(e) => handleDeleteChat(e, chat.uuid)}
              okText="Yes"
              cancelText="No"
              placement={isMobile ? "top" : "topRight"}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                className="delete-chat-button"
                style={{ 
                  marginLeft: '8px'
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </div>
        ),
        icon: <MessageOutlined />
      }));
      
      setChatItems(chatMenuItems);
    } catch (error) {
      console.error('Failed to fetch chat list:', error);
    }
  };

  // Handle menu item click
  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === '/logout') {
      localStorage.clear();
      window.location.href = "/login";
    } else {
      window.location.href = key;
    }
  };

  // Build menu items
  const items: MenuItem[] = [
    {
      key: '/',
      icon: <HomeOutlined style={{ fontSize: '18px' }} />,
      label: 'New Chat',
    },
    {
      key: 'chats',
      icon: <MessageOutlined style={{ fontSize: '18px' }} />,
      label: 'Recent Chats',
      children: chatItems,
    },
    {
      key: '/settings',
      icon: <SettingOutlined style={{ fontSize: '18px' }} />,
      label: 'Settings',
    },
  ];

  // Add user profile and logout for mobile
  if (isMobile) {
    items.push(
      {
        type: 'divider',
      },
      {
        key: 'user',
        icon: <UserOutlined style={{ fontSize: '18px' }} />,
        label: username,
        disabled: true,
      },
      {
        key: '/logout',
        icon: <LogoutOutlined style={{ fontSize: '18px' }} />,
        label: 'Logout',
        danger: true,
      }
    );
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            darkItemBg: 'var(--primary-bg-color)',
            darkSubMenuItemBg: 'var(--primary-bg-color)',
            colorBgBase: 'var(--primary-bg-color)',
            colorBgContainer: 'var(--primary-bg-color)',
            colorPrimaryBg: 'var(--primary-bg-color)',
            // Hover and active states
            darkItemSelectedBg: 'var(--secondary-bg-color)',
            darkItemHoverBg: 'var(--secondary-bg-color)',
            // Text colors
            darkItemColor: 'var(--text-color)',
            darkItemSelectedColor: 'var(--primary-color)',
            darkItemHoverColor: 'var(--primary-color)',
            zIndexPopup: -1,
          },
        },
      }}
    >
      <div style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '16px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'var(--primary-bg-color)',
          flexShrink: 0
        }}>
          {!collapsed && (
            <Typography.Title level={5} style={{ margin: 0, color: 'white', width: '100%' }}>
              Tommy Chat
            </Typography.Title>
          )}
          <Button 
            type="text" 
            onClick={toggleCollapsed}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            style={{ color: 'var(--primary-color)' }}
          />
        </div>
        
        <div style={{ 
          flex: 'auto', 
          overflow: 'auto',
          minHeight: 0,
        }}>
          <Menu
            defaultSelectedKeys={[window.location.pathname]}
            defaultOpenKeys={['chats']}
            mode="inline"
            theme="dark"
            inlineCollapsed={collapsed}
            items={items}
            onClick={handleMenuClick}
            triggerSubMenuAction="click"
            forceSubMenuRender={false}
          />
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Sidebar;